import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPinIcon, ShieldCheckIcon, SparklesIcon, CheckCircle2Icon, MessageSquareIcon, PhoneIcon, HeartIcon, Share2Icon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { ListingCardProps } from './ListingCard';
interface ListingDetailModalProps {
  listing: ListingCardProps;
  open: boolean;
  onClose: () => void;
}
export function ListingDetailModal({
  listing,
  open,
  onClose
}: ListingDetailModalProps) {
  const [activeImage, setActiveImage] = useState(0);
  const [saved, setSaved] = useState(listing.isSaved ?? false);
  const aiSummary = ['Fair price based on current market comparison', 'GCC specifications confirmed', 'Mileage is reasonable for the model year', 'Seller response rate is high'];
  const prev = () => setActiveImage(i => (i - 1 + listing.images.length) % listing.images.length);
  const next = () => setActiveImage(i => (i + 1) % listing.images.length);
  const sellerBadge = listing.sellerType === 'Verified Business' ? 'Verified Dealer' : listing.sellerType;
  const mountNode = typeof document !== 'undefined' && document.getElementById('app-screen-root') || (typeof document !== 'undefined' ? document.body : null);
  if (!mountNode) return null;
  return createPortal(<AnimatePresence data-id="element-1754">
      {open && <motion.div initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'spring',
      damping: 30,
      stiffness: 300
    }} className="absolute inset-0 z-[100] bg-white flex flex-col overflow-hidden" role="dialog" aria-modal="true" aria-label={listing.title} data-id="element-1755">
          {/* App header with back button */}
          <header className="flex-shrink-0 sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-100 pt-12 pb-3 px-3 flex items-center gap-2" data-id="element-1756">
            <button onClick={onClose} aria-label="Back" className="p-2 -ml-1 rounded-full hover:bg-slate-100 transition-colors text-slate-700" data-id="element-1757">
              <ChevronLeftIcon className="w-6 h-6" data-id="element-1758" />
            </button>
            <h1 className="flex-1 min-w-0 font-display font-bold text-[17px] text-dark-navy truncate" data-id="element-1759">
              {listing.title}
            </h1>
            <button onClick={() => setSaved(s => !s)} aria-label={saved ? 'Unsave' : 'Save'} className="p-2 rounded-full hover:bg-slate-100 transition-colors" data-id="element-1760">
              <HeartIcon className={`w-5 h-5 ${saved ? 'fill-error text-error' : 'text-slate-600'}`} data-id="element-1761" />
            </button>
            <button aria-label="Share" className="p-2 rounded-full hover:bg-slate-100 transition-colors text-slate-600" data-id="element-1762">
              <Share2Icon className="w-5 h-5" data-id="element-1763" />
            </button>
          </header>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto" data-id="element-1764">
            {/* Gallery */}
            <div className="relative aspect-[4/3] bg-slate-100" data-id="element-1765">
              <img src={listing.images[activeImage]} alt={listing.title} className="w-full h-full object-cover" data-id="element-1766" />
              {listing.images.length > 1 && <>
                  <button onClick={prev} aria-label="Previous image" className="absolute left-3 top-1/2 -translate-y-1/2 p-2 bg-white/85 backdrop-blur-sm rounded-full shadow-sm active:scale-95 transition-transform" data-id="element-1767">
                    <ChevronLeftIcon className="w-5 h-5 text-slate-700" data-id="element-1768" />
                  </button>
                  <button onClick={next} aria-label="Next image" className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-white/85 backdrop-blur-sm rounded-full shadow-sm active:scale-95 transition-transform" data-id="element-1769">
                    <ChevronRightIcon className="w-5 h-5 text-slate-700" data-id="element-1770" />
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5" data-id="element-1771">
                    {listing.images.map((_, idx) => <button key={idx} onClick={() => setActiveImage(idx)} aria-label={`Image ${idx + 1}`} className={`h-1.5 rounded-full transition-all ${idx === activeImage ? 'bg-white w-4' : 'bg-white/50 w-1.5'}`} data-id="element-1772" />)}
                  </div>
                </>}
              <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-sm" data-id="element-1773">
                <SparklesIcon className="w-4 h-4 text-ai-purple" data-id="element-1774" />
                <span className="text-sm font-bold bg-gradient-to-r from-primary-indigo to-ai-purple bg-clip-text text-transparent" data-id="element-1775">
                  {listing.matchScore}% Match
                </span>
              </div>
            </div>

            <div className="p-5 space-y-6" data-id="element-1776">
              {/* Title + Price */}
              <div data-id="element-1777">
                <h2 className="font-display font-bold text-[22px] leading-tight text-dark-navy mb-1.5" data-id="element-1778">
                  {listing.title}
                </h2>
                <div className="font-display font-extrabold text-[26px] text-primary-indigo mb-2" data-id="element-1779">
                  {listing.price}
                </div>
                <div className="flex items-center gap-1.5 text-slate-500 text-sm" data-id="element-1780">
                  <MapPinIcon className="w-4 h-4 flex-shrink-0" data-id="element-1781" />
                  <span data-id="element-1782">{listing.location}</span>
                </div>
              </div>

              {/* AI Summary */}
              <div className="bg-gradient-to-br from-primary-indigo/5 via-ai-purple/5 to-electric-cyan/5 rounded-2xl p-4 border border-primary-indigo/10" data-id="element-1783">
                <div className="flex items-center gap-2 mb-3.5" data-id="element-1784">
                  <div className="p-2 bg-gradient-to-br from-primary-indigo to-ai-purple rounded-xl" data-id="element-1785">
                    <SparklesIcon className="w-4 h-4 text-white" data-id="element-1786" />
                  </div>
                  <h3 className="font-display font-bold text-dark-navy" data-id="element-1787">
                    AI Analysis
                  </h3>
                </div>
                <ul className="space-y-2.5" data-id="element-1788">
                  {aiSummary.map((item, idx) => <li key={idx} className="flex items-start gap-2.5 text-slate-700 text-sm" data-id="element-1789">
                      <CheckCircle2Icon className="w-5 h-5 text-success flex-shrink-0 mt-0.5" data-id="element-1790" />
                      <span data-id="element-1791">{item}</span>
                    </li>)}
                </ul>
              </div>

              {/* Key info grid */}
              <div data-id="element-1792">
                <h3 className="font-display font-bold text-lg text-dark-navy mb-3" data-id="element-1793">
                  Key Information
                </h3>
                <div className="grid grid-cols-2 gap-3" data-id="element-1794">
                  {listing.specs.map((spec, idx) => <div key={idx} className="bg-slate-50 rounded-xl px-4 py-3" data-id="element-1795">
                      <span className="block text-xs text-slate-500 mb-1" data-id="element-1796">
                        {spec.label}
                      </span>
                      <span className="font-medium text-slate-900" data-id="element-1797">
                        {spec.value}
                      </span>
                    </div>)}
                </div>
              </div>

              {/* Seller */}
              <div className="bg-white rounded-2xl p-4 border border-slate-200" data-id="element-1798">
                <div className="flex items-center gap-4" data-id="element-1799">
                  <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center text-lg font-bold text-slate-400 flex-shrink-0" data-id="element-1800">
                    {listing.title.charAt(0)}
                  </div>
                  <div className="min-w-0" data-id="element-1801">
                    <h4 className="font-bold text-dark-navy" data-id="element-1802">{sellerBadge}</h4>
                    <div className="flex items-center gap-1.5 text-sm mt-0.5" data-id="element-1803">
                      {listing.sellerType === 'Verified Business' ? <>
                          <ShieldCheckIcon className="w-4 h-4 text-success flex-shrink-0" data-id="element-1804" />
                          <span className="text-success font-medium" data-id="element-1805">
                            Trusted · 99% response rate
                          </span>
                        </> : <span className="text-slate-500" data-id="element-1806">
                          Private seller · replies within hours
                        </span>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sticky bottom actions — Message, Call, Share */}
          <div className="flex-shrink-0 border-t border-slate-100 px-4 pt-3 pb-7 bg-white grid grid-cols-3 gap-2.5" data-id="element-1807">
            <button className="flex flex-col items-center justify-center gap-1 py-2.5 bg-primary-indigo text-white rounded-2xl font-semibold active:scale-[0.98] transition-transform" data-id="element-1808">
              <MessageSquareIcon className="w-5 h-5" data-id="element-1809" />
              <span className="text-[13px]" data-id="element-1810">Message</span>
            </button>
            <button className="flex flex-col items-center justify-center gap-1 py-2.5 bg-[#25D366] text-white rounded-2xl font-semibold active:scale-[0.98] transition-transform" data-id="element-1811">
              <PhoneIcon className="w-5 h-5" data-id="element-1812" />
              <span className="text-[13px]" data-id="element-1813">Call</span>
            </button>
            <button className="flex flex-col items-center justify-center gap-1 py-2.5 bg-slate-100 text-slate-700 rounded-2xl font-semibold active:scale-[0.98] transition-transform" data-id="element-1814">
              <Share2Icon className="w-5 h-5" data-id="element-1815" />
              <span className="text-[13px]" data-id="element-1816">Share</span>
            </button>
          </div>
        </motion.div>}
    </AnimatePresence>, mountNode);
}